import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Icon, Loader } from "semantic-ui-react";

import { AdvisedCriticalHistory, AppointmentPin, CUSTOM_CONFIRM_TYPES, CopyToClipboard, CustomConfirm, DATE_FORMATS_FNS, ScrollToSectionMenu } from "components";
import { CUSTOM_REASON } from "components/CustomConfirm/components";
import { setShowPriceInVat, usePermanentRealTimeUser, useScrollPageToTop, useShowPriceInVat } from "hooks";
import { DMS_CAPABILITIES, STATUS_IDENTIFIERS } from "models";
import "modules/AppointmentDetails/AppointmentDetails.scss";
import {
  ActivityLog,
  AppointmentDate,
  AppointmentNotes,
  AppointmentStatus,
  CarDetails,
  Checks,
  CopyContent,
  CustomerActions,
  IndicatorsAction,
  Interventions,
  SnoozedItems,
  VehicleTyres
} from "modules/AppointmentDetails/components";
import { useGroupedChecklists } from "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists/hooks";
import {
  useAppointmentLocation,
  useCancelAppointmentMutation,
  useCancellationReasonsQuery,
  useRealTimeAppointmentQuery,
  useRealTimeOrderedChecks,
  useScrollToSectionMenus
} from "modules/AppointmentDetails/hooks";
import { isTyreStatusVisibleOnWo } from "modules/AppointmentDetails/utils";
import { useLogout, usePermanentSession, useRedirectToLandingPage } from "modules/Login/hooks";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { RedirectProps } from "react-router-dom";

export const AppointmentDetails = () => {
  const { t } = useTranslation();
  const router = useHistory();

  const [isExpandedByDefaultExpanded, setIsExpandedByDefaultExpanded] = useState(true);
  const [isCollapsedByDefaultExpanded, setIsCollapsedByDefaultExpanded] = useState(false);
  const [showCancelModel, setShowCancelModel] = useState(false);
  const [cancelReason, setCancelReason] = useState<number | undefined>(undefined);
  const [customCancelReason, setCustomCancelReason] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isBackPressed, setIsBackPressed] = useState(false);

  const location = useLocation<RedirectProps | undefined>();
  const { appointment, loading, error } = useRealTimeAppointmentQuery();
  const { checks } = useRealTimeOrderedChecks(appointment?.id);
  const appointmentLocation = useAppointmentLocation(appointment);
  const { session } = usePermanentSession();
  const { user } = usePermanentRealTimeUser();
  const logout = useLogout();
  const redirectToLandingPage = useRedirectToLandingPage();
  const appointmentCancellationReasons = useCancellationReasonsQuery();
  const { mutate: cancelAppointment } = useCancelAppointmentMutation();
  const { data: checklists } = useGroupedChecklists();
  const showVat = useShowPriceInVat();
  const isTyreStatusVisible = isTyreStatusVisibleOnWo(appointmentLocation, checks);
  const scrollToSectionMenus = useScrollToSectionMenus(isTyreStatusVisible, appointment, checks);

  useScrollPageToTop();

  useEffect(() => {
    if (!error) return;
    toast.error(t("v8_error_loading_appointment", "Something went wrong loading the appointment. Please try again"));
  }, [error]);

  useEffect(() => {
    if (appointmentLocation) setShowPriceInVat(Boolean(appointmentLocation.include_vat && user?.is_vat_enabled_on_wo));
  }, [appointmentLocation?.id]);

  useEffect(() => {
    return () => {
      setIsBackPressed(false);
    };
  }, []);

  const handleConfirm = () => {
    setShowConfirmModal(false);
    setHasChanges(false);
    router.goBack();
  };

  const toggleExpand = () => {
    setIsExpandedByDefaultExpanded(prev => !prev);
    setIsCollapsedByDefaultExpanded(!isExpandedByDefaultExpanded);
  };

  const toggleShowVat = () => setShowPriceInVat(!showVat);
  const onRequestCloseModal = () => setShowCancelModel(false);
  const handleCancelAppointment = () => setShowCancelModel(true);
  const onRequestCloseHasChangeModal = () => setShowConfirmModal(false);
  const navigateBack = () => {
    if (hasChanges) {
      setShowConfirmModal(true);
      return;
    }

    setIsBackPressed(true);
    const from = location.state?.from;
    const isNavigatedFromLoginPage = from === PATHS.LOGIN;

    if (window.history.length > 1 && !isNavigatedFromLoginPage) from ? router.replace(from) : router.goBack();
    else {
      if (isNavigatedFromLoginPage) window.history.replaceState(null, "");
      redirectToLandingPage();
    }
  };

  if (loading)
    return (
      <div className="AppointmentDetails-loader">
        <Loader active inline />
      </div>
    );

  if (!appointment || !appointmentLocation) return;

  const handleCancelConfirm = () => {
    if (!cancelReason) return;

    cancelAppointment({
      appointment_id: appointment.id,
      reason_id: cancelReason > CUSTOM_REASON ? cancelReason : undefined,
      custom_reason: customCancelReason ? customCancelReason : undefined
    });

    setShowCancelModel(false);
  };

  return (
    <div className={`AppointmentDetails ${appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus ? "CanceledAppointment" : ""}`}>
      <div className="AppointmentDetails-container">
        <div className="AppointmentDetails-NavigationHeader">
          <div className="AppointmentDetails-NavigationHeader-container">
            <div className="row">
              <AppointmentStatus appointment={appointment} checklists={checklists} />
              {appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus && (
                <IndicatorsAction appointment={appointment} showVat={showVat} onSwitchShowVat={toggleShowVat} />
              )}
            </div>
            <div className="row tabs-row-container">
              <ScrollToSectionMenu sectionMenus={scrollToSectionMenus} offset={-280} hideBorder />
              <div className="sub-row">
                {appointment.car?.reg_nr && (
                  <div className="Appointment-reg-nr">
                    <CopyToClipboard text={appointment.car.reg_nr} onSuccess={() => toast.success(t("v8_copy_to_clipboard", "Copied to clipboard"))} />
                  </div>
                )}
                <div className="Appointment-time">
                  <div>
                    <strong>{t("v8_appointment", "Appointment")}</strong>
                    <AppointmentDate appointment={appointment} />
                  </div>

                  {(appointment.is_local || appointmentLocation.dms_capability_ids?.includes(DMS_CAPABILITIES.ScheduledInOut)) && (
                    <>
                      {appointment.due_in && (
                        <div>
                          <strong>{t("v8_in", "In")}</strong>
                          {appointment.due_in && formatDate(appointment.due_in, DATE_FORMATS_FNS.dateMonthYearTime)}
                        </div>
                      )}

                      {appointment.car_return_time && (
                        <div>
                          <strong>{t("v8_out", "Out")}</strong>
                          {appointment.car_return_time && formatDate(appointment.car_return_time, DATE_FORMATS_FNS.dateMonthYearTime)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="Appointment-time">
                  <CopyContent content={appointment.wo_nr}>
                    <div>
                      <strong>{t("v8_wo", "WO")} #</strong>
                      <span className={appointment.internal ? "info-wo" : ""}>{appointment.wo_nr}</span>
                      <span className="appointment-pin ml-5">
                        <AppointmentPin appointment={appointment} location={appointmentLocation} />
                      </span>
                    </div>
                  </CopyContent>
                </div>
                <Button className="-appointment-status icon" size="small" onClick={toggleExpand}>
                  <Icon className={`arrows ${isExpandedByDefaultExpanded ? "minimize" : "maximize"}`} color="green" />
                </Button>
                {!session?.appointment_id && (
                  <Button className="-appointment-status" size="small" active loading={isBackPressed} disabled={isBackPressed} onClick={navigateBack}>
                    <Icon className="angle left" />
                    {t("v8_back", "Back")}
                  </Button>
                )}
                {!!session?.appointment_id && (
                  <Button className="-appointment-status" size="small" active onClick={logout}>
                    <Icon className="angle left" />
                    {t("v8_logout", "Logout")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="AppointmentDetails-cards mt-20">
          <CarDetails
            sectionMenu={scrollToSectionMenus.details}
            appointment={appointment}
            isExpanded={isExpandedByDefaultExpanded}
            handleCancelAppointment={handleCancelAppointment}
          />
          <CustomerActions sectionMenu={scrollToSectionMenus.customerActions} isExpanded={isExpandedByDefaultExpanded} appointment={appointment} />
          <SnoozedItems sectionMenu={scrollToSectionMenus.snoozedItems} isExpanded={isExpandedByDefaultExpanded} appointment={appointment} checks={checks} />
          <AdvisedCriticalHistory
            sectionMenu={scrollToSectionMenus.advisedCriticalHistory}
            isExpanded={isCollapsedByDefaultExpanded}
            appointment={appointment}
            carId={appointment.car_id}
          />
          <AppointmentNotes sectionMenu={scrollToSectionMenus.notes} appointment={appointment} isExpanded={isExpandedByDefaultExpanded} />
          <Interventions
            sectionMenu={scrollToSectionMenus.interventions}
            isExpanded={isExpandedByDefaultExpanded}
            appointment={appointment}
            appointmentLocation={appointmentLocation}
            hasChanges={hasChanges}
            setHasChanges={setHasChanges}
            checklists={checklists}
          />
          <VehicleTyres
            sectionMenu={scrollToSectionMenus.tyreReplacements}
            isExpanded={isExpandedByDefaultExpanded}
            isTyreStatusVisible={isTyreStatusVisible}
            appointment={appointment}
          />
          <Checks sectionMenu={scrollToSectionMenus.checks} isExpanded={isExpandedByDefaultExpanded} appointment={appointment} checks={checks} />
          <ActivityLog sectionMenu={scrollToSectionMenus.activityLogs} isExpanded={isCollapsedByDefaultExpanded} appointment={appointment} />
        </div>

        <CustomConfirm
          type={CUSTOM_CONFIRM_TYPES.Danger}
          confirmReasons={appointmentCancellationReasons.data}
          allowCustomReason
          isOpen={showCancelModel}
          customClass={"CancelAppointmentModal"}
          handleCancel={onRequestCloseModal}
          handleConfirm={handleCancelConfirm}
          handleReasonChange={value => setCancelReason(value)}
          handleChangeCustomReason={e => setCustomCancelReason(e.target.value)}
          isLoading={appointmentCancellationReasons.isLoading}
          confirmMsg={t("v8_confirm_cancel_appointment_message", "Are you sure that you want to cancel this appointment?")}
          error={appointmentCancellationReasons.error?.message ?? ""}
          cancelButtonText={t("v8_close", "Close")}
          confirmButtonText={t("v8_confirm", "Confirm")}
          placeholderText={t("v8_cancellation_reason", "Cancellation reason")}
        />
        <CustomConfirm
          isLoading={false}
          type={CUSTOM_CONFIRM_TYPES.Warning}
          isOpen={showConfirmModal}
          handleConfirm={handleConfirm}
          handleCancel={onRequestCloseHasChangeModal}
          confirmMsg={t("v8_unsaved_changes_confirm_message", "You have some unsaved changes. If you proceed, these changes will be lost.")}
        />
      </div>
    </div>
  );
};
